<template>
  <div :class="mainCss">
    <transition name="show-bg-overlay">
      <div
        v-show="open"
        ref="bgLayer"
        class="cat-modal-bg-overlay"
        @click="bgClick"
      ></div>
    </transition>
    <transition
      name="show-modal"
      @after-enter="afterEnter"
      @after-leave="afterLeave"
    >
      <div
        v-show="open"
        :id="id"
        ref="modal"
        v-focus-trap
        aria-modal="true"
        :class="_class"
        :role="isAlert ? 'alertdialog' : 'dialog'"
        :aria-labelledby="id ? id + '_modal_heading' : undefined"
        :aria-describedby="isAlert ? id + '_modal_content' : undefined"
        :data-label="modalTitle"
        @keydown.tab="setFocus"
        @keydown.esc="close($event, closeOnEsc)"
      >
        <cat-btn
          v-if="showClose"
          ref="topCloseButton"
          :class="classClose"
          icon="cancel"
          :hint="!showCloseLabel ? closeLabel : null"
          :data-label="closeLabel"
          @ui-click="close($event, true)"
          ><span v-if="showCloseLabel">{{ closeLabel }}</span></cat-btn
        >
        <div class="modal-content-container content">
          <div v-if="heading" class="modal-heading">
            <h1
              :id="id + '_modal_heading'"
              ref="modalHeading"
              tabindex="-1"
              :data-label="heading"
              :class="help && !!help.url ? 'inline' : null"
            >
              {{ heading }}
            </h1>
            <cat-help-trigger
              v-if="help && help.url"
              :sourceId="helpSourceId"
              :url="help.url"
              :title="heading"
            />
          </div>
          <div
            :id="id + '_modal_content'"
            class="modal-content"
            :style="{ 'padding-bottom': paddingContent + 'px' }"
          >
            <slot></slot>
          </div>
        </div>
        <div v-if="hasButtons()" ref="buttonPad" :class="classButtonPad">
          <slot name="buttons"> </slot>
        </div>
      </div>
    </transition>
  </div>
</template>
<script lang="ts">
import { defineComponent } from "vue";
import jQuery from "jquery";
import CatBtn from "@/components/catBtn/catBtn.vue";
import CatHelpTrigger from "@/components/catPageTemplate/catHelpTrigger.vue";
import ModalMixin from "../utilities/modalMixin.js";
export default defineComponent({
  name: "CatModal",
  mixins: [ModalMixin],
  components: { CatBtn, CatHelpTrigger },
  provide: function () {
    return {
      registerTabDescendant: false
    };
  },
  props: {
    id: [String],
    open: [Boolean],
    callerId: {
      type: String,
      description: "Element id that invoked the modal"
    },
    size: {
      type: String,
      description:
        "Values: small, medium, large, extra-large, full. Sizes small and full are applicable to modal dialog variation only."
    },
    fixedSize: {
      type: Boolean,
      default: false,
      description:
        "Renders the modal in fixed size. If not set, the width is the percentage with respect to the device screen with scrollbars. Applicable to small and medium modal dialog variation only."
    },
    sidepanel: {
      type: Boolean,
      default: false,
      description:
        "The modal overlay is rendered as a side panel with slideout animation."
    },
    isAlert: {
      type: Boolean,
      default: false,
      description:
        "Whether to display the modal as an alert dialog or a regular modal."
    },
    showClose: {
      type: Boolean,
      default: true,
      description:
        "Show the modal top close button. Selecting the button will trigger the event 'ui-close'"
    },
    showCloseLabel: {
      type: Boolean,
      default: true,
      description: "Show the close button text label"
    },
    closeLabel: {
      type: String,
      default: "Close",
      description: "Modal close button text label"
    },
    closeOnEsc: {
      type: Boolean,
      default: true,
      description: "Indicates whether the modal closes on Esc key press."
    },
    heading: {
      type: String,
      description: "Heading text of the modal"
    },
    title: {
      type: String,
      description:
        "Title to identify the modal by assistive technology. If not present, this will fallback to the heading if available."
    },
    help: {
      type: Object,
      description: "Help details for this modal."
    },
    buttonPad: {
      type: Boolean,
      default: false,
      description: "Renders the button pad styling"
    },
    reverseButtons: {
      type: Boolean,
      default: false,
      description: "Reverse the order of the primary button in the button pad."
    },
    hasApiButtons: {
      type: Boolean,
      default: true,
      description:
        "Defaults to true and set to false if there is no button from child-service-modal API provider."
    }
  },
  inject: { resolveTxt: { name: "resolveTxt", default: (key, def) => def } },
  watch: {
    open: function (nw, ol) {
      this._open(true);
    }
  },
  data: function () {
    return {
      modalIndex: 0,
      paddingContent: ""
    };
  },
  mounted: function () {
    if (this.open) {
      this._open(true);
    }

    // Set the nested index
    this.$nextTick(function () {
      this.modalIndex = this.getModalIndex();
    });
  },
  beforeUnmount: function () {
    this.removeBodyClass();
  },
  computed: {
    mainCss: function () {
      return {
        "cat-modal-container": true,
        ["modal-index-" + this.modalIndex]: true
      };
    },
    _class: function () {
      return {
        "cat-modal": true,
        open: this.open,
        dialog: !this.sidepanel,
        sidepanel: this.sidepanel,
        "fixed-size": this.fixedSize,
        "modal-size-s": this.size === "small",
        "modal-size-m": this.size === "medium",
        "modal-size-l": this.size === "large",
        "modal-size-xl": this.size === "extra-large",
        "modal-size-full": this.size === "full"
      };
    },
    classClose: function () {
      return {
        small: true,
        "top-close": true
      };
    },
    classButtonPad: function () {
      return {
        "modal-button-pad": true,
        "button-pad": true,
        "page-button-pad": this.buttonPad,
        reverse: this.reverseButtons
      };
    },
    modalTitle: function () {
      return this.title ? this.title : this.heading ? this.heading : null;
    },
    helpSourceId: function () {
      return this.callerId ? this.callerId : this.id ? this.id : null;
    }
  },
  methods: {
    afterEnter: function (el) {
      this.setInitialFocus();
      this.$emit("ui-opened");
      this.setContentPadding(true);
    },
    afterLeave: function (el) {
      this.$emit("ui-closed");
      this.setContentPadding(false);
    },
    close: function (evt, close) {
      if (close === null || close) {
        this.$emit("ui-close", {
          id: this.id ? this.id : null,
          caller: this.callerId ? this.callerId : null
        });
      }
      this.removeBodyClass();
    },
    confirm: function (evt) {
      this.$emit("ui-confirm", {
        id: this.id,
        confirm: true,
        caller: this.callerId ? this.callerId : null
      });
    },
    cancel: function (evt) {
      this.$emit("ui-cancel", {
        id: this.id,
        confirm: false,
        caller: this.callerId ? this.callerId : null
      });
    },
    _open: function (focus) {
      if (this.open) {
        if (typeof this.addBodyClass === "function") {
          this.addBodyClass();
        }
      } else {
        if (typeof this.removeBodyClass === "function") {
          this.removeBodyClass();
        }
      }
    },
    /**
     * See component accessibility for more details on the focus
     * 1. If the top close button is present, set the focus on the top close button
     * 2. Last option, set the focus on the first focusable element in the page.
     */
    setInitialFocus: function () {
      // 1. Top close button
      var focusItem =
        this.$refs.topCloseButton && jQuery(this.$refs.topCloseButton.$el);
      if (!focusItem || focusItem.length === 0) {
        // 2. Any focusable element
        focusItem = window.jQuery(":tabbable", this.$refs.modal);
      }
      if (focusItem.length > 0) {
        focusItem.first().focus();
      }
    },
    setFocus: function (event) {
      var firstItem = window.jQuery(":tabbable", this.$refs.modal).first();
      var lastItem = window.jQuery(":tabbable", this.$refs.modal).last();
      var target = jQuery(event.target).first();

      // If the tab event is fired from the first or the last item in the modal,
      // trap the focus inside the modal
      if (firstItem.is(lastItem)) {
        event.preventDefault();
        firstItem.focus();
      } else if (target.is(firstItem)) {
        // Tabbing to previous, focus to the last
        if (event.shiftKey) {
          event.preventDefault();
          lastItem.focus();
        }
      } else if (target.is(lastItem)) {
        // Tabbing to the next item, focus to the first
        if (!event.shiftKey) {
          event.preventDefault();
          firstItem.focus();
        }
      }
    },
    getModalIndex: function () {
      // See if this modal is inside another modal and get the nested level
      const count = jQuery(this.$el).parents(".cat-modal-container").length;
      return count;
    },
    bgClick: function (event) {
      this.$emit("bgclick", event);
      event.preventDefault();
      this.setInitialFocus();
    },
    /**
     * Show/Hide buttons
     * 1. If slot is not undefined
     * 2. if there are buttons from child-service api provider
     */
    hasButtons: function () {
      return typeof this.$slots.buttons !== "undefined" && this.hasApiButtons;
    },
    /**
     * Additional modal padding-bottom
     * 1. If modal is sidepanel
     * 2. if it has buttons
     */
    setContentPadding: function (option) {
      if (this.hasButtons && this.sidepanel) {
        if (option) {
          const buttonHeight = jQuery(this.$refs.buttonPad).outerHeight();
          this.paddingContent = buttonHeight;
        } else {
          this.paddingContent = "";
        }
      }
    }
  }
});
</script>
